// The provider application as data: section list, option vocabularies, and the
// client-side required-field gate that mirrors the server's submit validation.
// (Server gate: firstName+lastName, licenseNumber, licenseExpiresOn, and — US
// only — licensedStates and acceptedInsurancePlans non-empty.)
import type { ProviderApplicationFields } from './providerApi';

export interface SectionDef {
  id: string;
  label: string; // shown in the progress affordance
  title: string;
  subtitle?: string;
}

export const SECTIONS: SectionDef[] = [
  {
    id: 'about',
    label: 'About you',
    title: "Let's start with the basics.",
    subtitle: 'Who you are and how we reach you while we review.',
  },
  {
    id: 'licensing',
    label: 'Licensing',
    title: 'Your license.',
    subtitle: 'We verify every license before a profile goes live — this is the part we check first.',
  },
  {
    id: 'practice',
    label: 'Practice',
    title: 'How you practice.',
    subtitle: 'This drives how we match you with the right patients.',
  },
  {
    id: 'profile',
    label: 'Profile',
    title: 'Your public profile.',
    subtitle: 'Patients read this when choosing a physical therapist. Write it the way you would say it.',
  },
  {
    id: 'payment',
    label: 'Payment',
    title: 'How patients pay you.',
    subtitle: 'Insurance you accept and your cash rates.',
  },
  {
    id: 'attestation',
    label: 'Attestation',
    title: 'Almost done.',
    subtitle: 'Two attestations, then your application goes to our review team.',
  },
];

// The 10 APTA board-certified specialty areas.
export const SPECIALTIES: string[] = [
  'Orthopedics',
  'Sports',
  'Geriatrics',
  'Pediatrics',
  'Neurology',
  'Oncology',
  "Women's Health",
  'Cardiovascular & Pulmonary',
  'Clinical Electrophysiology',
  'Wound Management',
];

export const SESSION_DURATIONS = [30, 45, 60] as const;

export interface MissingField {
  field: string;
  label: string;
  sectionIndex: number; // where to send the user to fix it
}

// Mirror of the server's submit gate. Returns [] when submittable.
export function missingRequirements(f: ProviderApplicationFields): MissingField[] {
  const missing: MissingField[] = [];
  if (!f.firstName?.trim()) missing.push({ field: 'firstName', label: 'First name', sectionIndex: 0 });
  if (!f.lastName?.trim()) missing.push({ field: 'lastName', label: 'Last name', sectionIndex: 0 });
  if (!f.licenseNumber?.trim()) missing.push({ field: 'licenseNumber', label: 'License number', sectionIndex: 1 });
  if (!f.licenseExpiresOn) missing.push({ field: 'licenseExpiresOn', label: 'License expiry date', sectionIndex: 1 });
  // State licensure and insurance panels are US-only, and so are their
  // requirements — the licensing section hides both questions outside the US, so
  // requiring them here would block submit on fields the applicant cannot see.
  const isUS = (f.country ?? 'US') === 'US';
  if (isUS && !f.licensedStates?.length) {
    missing.push({ field: 'licensedStates', label: 'At least one licensed state', sectionIndex: 1 });
  }
  if (isUS && !f.acceptedInsurancePlans?.length) {
    missing.push({ field: 'acceptedInsurancePlans', label: 'At least one accepted insurance plan', sectionIndex: 4 });
  }
  return missing;
}

// Strip the payload down to what's worth sending: drop undefined, empty strings,
// and fully-empty expertise cards. Arrays are sent as-is (an emptied array is a
// deliberate clear).
export function toPayload(f: ProviderApplicationFields): ProviderApplicationFields {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    out[k] = v;
  }
  if (Array.isArray(f.expertiseAreas)) {
    out.expertiseAreas = f.expertiseAreas.filter((e) => e.title.trim() || e.description.trim());
  }
  return out as ProviderApplicationFields;
}
